import { useEffect, useRef } from 'react';

export interface UseAutoScrollOptions {
  /**
   * 是否启用自动滚动
   * @default true
   */
  enabled?: boolean;
  /**
   * 判断是否在底部的误差范围（像素）
   * @default 50
   */
  threshold?: number;
  /**
   * 依赖项数组，当这些值变化时触发滚动检查
   */
  dependencies?: any[];
  /**
   * 强制滚动的触发条件函数
   * 返回 true 时会强制滚动到底部，忽略用户滚动状态
   */
  shouldForceScroll?: () => boolean;
}

/**
 * 自动滚动 Hook
 *
 * 功能：
 * 1. 默认自动滚动到底部
 * 2. 检测用户手动滚动，如果用户向上滚动则暂停自动滚动
 * 3. 用户滚动回底部时恢复自动滚动
 * 4. 支持强制滚动（如发送新消息时）
 *
 * @example
 * ```tsx
 * const containerRef = useAutoScroll({
 *   dependencies: [messages, isLoading],
 *   shouldForceScroll: () => {
 *     const lastMessage = messages[messages.length - 1];
 *     return lastMessage?.role === 'user';
 *   }
 * });
 *
 * return <div ref={containerRef}>...</div>
 * ```
 */
export function useAutoScroll<T extends HTMLElement = HTMLDivElement>(
  options: UseAutoScrollOptions = {},
) {
  const {
    enabled = true,
    threshold = 50,
    dependencies = [],
    shouldForceScroll,
  } = options;

  const containerRef = useRef<T>(null);
  const isUserScrollingRef = useRef(false); // 用户是否手动滚动过
  const isAutoScrollingRef = useRef(false); // 是否正在自动滚动
  const previousDepsRef = useRef<any[]>(dependencies);

  // 检查是否在底部
  const isAtBottom = () => {
    if (!containerRef.current) {
 return false;
}
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  // 滚动到底部的函数
  const scrollToBottom = (force = false) => {
    if (!enabled || !containerRef.current) {
      return;
    }

    // 如果用户手动滚动过且不是强制滚动，则不自动滚动
    if (isUserScrollingRef.current && !force) {
      return;
    }

    isAutoScrollingRef.current = true;

    // 使用 requestAnimationFrame 确保 DOM 更新后再滚动
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;

        // 延迟解锁
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 100);
      }
    });
  };

  // 监听用户手动滚动
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
 return;
}

    const handleScroll = () => {
      // 如果正在自动滚动，忽略此事件
      if (isAutoScrollingRef.current) {
        return;
      }

      // 检查用户是否滚动到底部
      if (isAtBottom()) {
        // 用户滚到底部，恢复自动滚动
        isUserScrollingRef.current = false;
      } else {
        // 用户向上滚动，停止自动滚动
        isUserScrollingRef.current = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [enabled, threshold]);

  // 当依赖项变化时，检查是否需要滚动
  useEffect(() => {
    // 检查是否需要强制滚动
    if (shouldForceScroll?.()) {
      isUserScrollingRef.current = false;
      scrollToBottom(true);
    } else {
      scrollToBottom();
    }

    previousDepsRef.current = dependencies;
  }, dependencies);

  return {
    containerRef,
    scrollToBottom,
  };
}
